import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import axios from "axios";
import Modal from "react-modal";
import Loader from "./Loader";
import Icon from "./Icon";
import classNames from "classnames";

import IconElement from "./IconElement";
import peacocktv from "./images/peacocktv.svg";

import LEAGUES from "./Leagues";
import TEAMS from "./Teams";
import SPORTS from "./Sports";

import Footer from "./Footer";

Modal.setAppElement("#root");

const PREFERENCES_ID = window.location.search.split("?")[1];

let BASE_URL = `https://${window.location.host}/sport-preference/${PREFERENCES_ID}`;

if (process.env.NODE_ENV === "development") {
  BASE_URL = `https://sport-preferences.pickaxe.ai/sport-preference/${PREFERENCES_ID}`;
}

const App = () => {
  const methods = useForm({ defaultValues: {} });

  const [preferences, setPreferences] = useState({
    sports_league_preferences: [],
    sports_team_preferences: [],
    sports_preferences: [],
  });

  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState({
    type: null,
    value: false,
  });
  const [isSavingPreferences, setSavingPreferences] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(BASE_URL, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setLoading(false);
        setPreferences(response.data);

        // Default form values
        methods.reset(convertRequest(response.data));
      })
      .catch((e) => {
        setLoading(false);
        setError({
          type: "loading",
          value: true,
        });
      });
  }, [methods]);

  const convertRequest = (data) => {
    let preferences = [];

    Object.entries(data).forEach((item) =>
      item[1].forEach((item) => preferences.push(item)),
    );

    const formatted = preferences.reduce((acc, curr) => {
      acc[curr] = true;
      return acc;
    }, {});

    return formatted;
  };

  const convertResponse = (data) => {
    let template = {
      sports_league_preferences: [],
      sports_team_preferences: [],
      sports_preferences: [],
    };

    Object.entries(data).forEach(([id, value]) => {
      if (!value || id === "college") return; // Exclude 'college' since it does not have a matching sport under the LEAGUES

      if (SPORTS.some((o) => o.id === id)) {
        template.sports_preferences.push(id);
        return;
      }

      if (LEAGUES.some((o) => o.id === id)) {
        template.sports_league_preferences.push(id);
        return;
      }

      template.sports_team_preferences.push(id);
    });

    // Auto-select leagues and sports for any selected teams
    template.sports_team_preferences.forEach((teamId) => {
      for (const league of LEAGUES) {
        const leagueTeams = TEAMS[league.id + "_teams"] || [];
        const teamData = leagueTeams.find((t) => t.id === teamId);
        if (teamData) {
          if (!template.sports_league_preferences.includes(league.id)) {
            template.sports_league_preferences.push(league.id);
          }
          if (
            teamData.sport &&
            !template.sports_preferences.includes(teamData.sport)
          ) {
            template.sports_preferences.push(teamData.sport);
          }
          break;
        }
      }
    });

    return template;
  };

  const [modalTeams, setModalTeamsOpen] = useState(false);
  const [modalDone, setModalDoneOpen] = useState(false);
  const [modalError, setModalErrorOpen] = useState(false);
  const [currentLeague, setCurrentLeague] = useState();

  const onSubmit = (data) => {
    setSavingPreferences(true);
    setModalDoneOpen(true);
    const preferences = {};

    Object.keys(data).filter((key) => {
      if (data[key] && key !== "undefined") {
        return (preferences[key] = true);
      } else {
        return null;
      }
    });

    axios
      .post(BASE_URL, convertResponse(preferences))
      .then(() => {
        setSavingPreferences(false);
      })
      .catch((error) => {
        setSavingPreferences(false);
        setError({
          type: "saving",
          value: true,
        });
      });
  };

  const closeModal = () => {
    setModalTeamsOpen(false);
    setModalDoneOpen(false);
    setModalErrorOpen(false);

    setCurrentLeague(null);
  };

  const handleLeaguePreferences = (id, league, sport, event) => {
    const hasAtLeastOneTeam = preferences.sports_team_preferences.find((team) =>
      team.toLowerCase().startsWith(id),
    );
    const isCurrentlySelected =
      preferences.sports_league_preferences.includes(id);

    if (hasAtLeastOneTeam && isCurrentlySelected) {
      event.preventDefault();
    }

    let newPreferences = { ...preferences };
    const isSelected = newPreferences.sports_league_preferences.includes(id);

    if (isSelected) {
      // Can't remove a league if it has teams selected
      if (hasAtLeastOneTeam) {
        setModalErrorOpen({
          open: true,
          message:
            "You can't remove a league if you have selected at least one team.",
        });
      } else {
        // Remove
        newPreferences.sports_league_preferences =
          newPreferences.sports_league_preferences.filter(
            (league) => league !== id,
          );
        methods.setValue(league, false);
        setPreferences(newPreferences);

        if (hasAtLeastOneTeam) {
          methods.setValue(league, false);
        }
      }
    } else {
      // Add
      if (!newPreferences.sports_league_preferences.includes(id)) {
        newPreferences.sports_league_preferences.push(id);
        setPreferences(newPreferences);
        methods.setValue(id, true);
      }
    }
  };

  const renderLeaguePreferences = () =>
    LEAGUES.sort((a, b) => a["name"].localeCompare(b["name"])).map(
      (league, index) => {
        const leagueTeams = TEAMS[league.id + "_teams"] || [];
        const teams = preferences.sports_team_preferences.filter((team) =>
          leagueTeams.some((t) => t.id === team),
        );

        if (teams.length > 0) {
          return (
            <div key={index}>
              <IconElement
                eventHandler={handleLeaguePreferences}
                icon={league.icon}
                name={league.league_friendly_name}
                id={league.id}
                league={league.id}
                size={"lg"}
              />
              <div
                className={classNames(
                  "flex relative top-1 justify-center items-center space-x-1",
                  {
                    "cursor-pointer": true,
                    "opacity-50 cursor-not-allowed": false,
                  },
                )}
                onClick={() => {
                  setModalTeamsOpen(true);
                  setCurrentLeague(league.id);
                }}
              >
                {[0, 1, 2].map((_, index) => {
                  const teamData = teams[index]
                    ? leagueTeams.find((t) => t.id === teams[index])
                    : null;
                  if (teamData) {
                    return (
                      <div
                        key={index}
                        className="flex justify-center items-center w-1/3 h-14 rounded-sm bg-zinc-900"
                      >
                        <div className="w-8">
                          <Icon icon={teamData.icon} />
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div
                        key={index}
                        className="w-1/3 h-14 rounded-sm bg-zinc-900"
                      ></div>
                    );
                  }
                })}
              </div>
            </div>
          );
        } else {
          return (
            <div key={index}>
              <IconElement
                eventHandler={handleLeaguePreferences}
                icon={league.icon}
                name={league.league_friendly_name}
                id={league.id}
                league={league.id}
                size={"lg"}
              />
              <div
                onClick={() => {
                  setModalTeamsOpen(true);
                  setCurrentLeague(league.id);
                }}
                className={classNames(
                  "flex relative top-1 space-x-1 space-y-1",
                  {
                    "cursor-pointer": true,
                    "cursor-not-allowed": false,
                  },
                )}
              >
                <div
                  className={classNames(
                    "absolute top-6 w-full text-xs text-zinc-400",
                    { "opacity-50": false },
                  )}
                >
                  Select Your Favorites
                </div>
                <div className="w-1/3 h-14 rounded-sm bg-zinc-900"></div>
                <div className="w-1/3 h-14 rounded-sm bg-zinc-900"></div>
                <div className="w-1/3 h-14 rounded-sm bg-zinc-900"></div>
              </div>
            </div>
          );
        }
      },
    );

  // Teams
  const handleTeamsPreferences = (id, league, sport, event) => {
    let newPreferences = { ...preferences };

    const currentCount = preferences.sports_team_preferences.filter((team) =>
      team.startsWith(currentLeague.toUpperCase()),
    ).length;

    if (!event.target.checked && currentCount <= 3) {
      // Remove
      newPreferences.sports_team_preferences =
        newPreferences.sports_team_preferences.filter((sport) => sport !== id);

      setPreferences(newPreferences);
    } else {
      if (currentCount === 3) {
        event.preventDefault();
        setModalErrorOpen({
          open: true,
          message: "Please select up to 3 teams.",
        });
        methods.setValue(id, false);
      } else {
        // Add Team

        if (!newPreferences.sports_team_preferences.includes(id)) {
          newPreferences.sports_team_preferences.push(id);
          setPreferences(newPreferences);
        }

        // Add and select corresponding League
        if (!newPreferences.sports_league_preferences.includes(league)) {
          newPreferences.sports_league_preferences.push(league);
          setPreferences(newPreferences);
          methods.setValue(league, true);
        }

        // Add and select corresponding Sport
        if (!newPreferences.sports_preferences.includes(sport)) {
          newPreferences.sports_preferences.push(sport);
          setPreferences(newPreferences);
          methods.setValue(sport, true);
        }
      }
    }
  };

  const renderTeamsPreferences = () => {
    if (TEAMS[currentLeague + "_teams"]) {
      return TEAMS[currentLeague + "_teams"]
        .sort((a, b) => a["name"].localeCompare(b["name"]))
        .map((team, index) => {
          const isSelected = preferences.sports_team_preferences.includes(
            team.id,
          );
          return (
            <IconElement
              key={index + "__" + team.id}
              eventHandler={handleTeamsPreferences}
              isSelected={isSelected}
              icon={team.icon}
              name={team.name}
              id={team.id}
              league={team.league}
              sport={team.sport}
              size={"sm"}
            />
          );
        });
    }
  };

  // Sports
  const handleSportsPreferences = (id, league, sport, event) => {
    let newPreferences = { ...preferences };
    const teams = TEAMS[league + "_teams"];

    if (!event.target.checked) {
      // Check linked teams
      let sportWithLinkedTeams;

      if (preferences.sports_team_preferences.length && teams) {
        sportWithLinkedTeams = preferences.sports_team_preferences.some(
          (team) => teams.find((o) => o.id === team),
        );
      }

      // Sport can be removed only if it's not linked with any league
      if (sportWithLinkedTeams) {
        event.preventDefault();
        setModalErrorOpen({
          open: true,
          message:
            "You can't remove a sport if you have selected at least one team from its league.",
        });
      } else {
        // Remove
        newPreferences.sports_preferences =
          newPreferences.sports_preferences.filter((sport) => sport !== id);

        setPreferences(newPreferences);
      }
    } else {
      // Add
      if (!newPreferences.sports_preferences.includes(id)) {
        newPreferences.sports_preferences.push(id);
        setPreferences(newPreferences);
      }
    }
  };

  const renderSportPreferences = () =>
    SPORTS.sort((a, b) => a["name"].localeCompare(b["name"])).map(
      (sport, index) => {
        const isSelected = preferences.sports_preferences.includes(sport.id);

        return (
          <IconElement
            key={index}
            eventHandler={handleSportsPreferences}
            isSelected={isSelected}
            icon={sport.icon}
            name={sport.name}
            id={sport.id}
            sport={sport.sport}
            league={sport.league}
            size={"md"}
          />
        );
      },
    );

  if (isLoading) {
    return (
      <div className="grid justify-center items-center h-screen">
        <div>
          <p className="text-white">Loading Your Preference Center</p>
          <div className="flex justify-center items-center">
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  if (isError.type === "loading" && isError.value) {
    return (
      <div className="grid justify-center items-center h-screen">
        <div>
          <p className="mb-4 text-white">Couldn't Load Preference Center</p>
          <div className="flex justify-center items-center">
            <button
              onClick={() => window.location.reload(false)}
              className="btn"
            >
              Refresh and Try again.
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isError.type === "saving" && isError.value) {
    return (
      <div className="grid justify-center items-center h-screen">
        <div>
          <p className="mb-4 text-white">Couldn't Save Your Preferences.</p>
          <div className="flex justify-center items-center">
            <button
              onClick={() => window.location.reload(false)}
              className="btn"
            >
              Refresh and Try again.
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <nav className="sticky top-0 z-30 bg-black">
        <div className="flex justify-center items-center px-4 py-4 mr-auto ml-auto max-w-screen-lg text-white md:px-14">
          <div className="mr-auto text-left">
            <img
              src={peacocktv}
              alt="peacocktv"
              className="w-24 text-white md:w-36 w-100 fill-white"
            />
          </div>
          <div className="ml-auto text-right">
            <button
              type="submit"
              form="PreferencesForm"
              className="bg-yellow-300 hover:bg-white btn"
            >
              Save <span className="hidden md:inline-block">Preferences</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="p-4 mr-auto ml-auto max-w-screen-xl text-white">
        <form onSubmit={methods.handleSubmit(onSubmit)} id="PreferencesForm">
          <main className="text-center">
            <h1 className="my-5 text-5xl font-extrabold">Pick Your Faves</h1>

            <p className="mr-auto mb-10 ml-auto max-w-lg text-md">
              Keep up with the teams and sports that you love. Get updates on
              what's happening and what's ahead.
            </p>

            <div className="flex flex-wrap gap-4 justify-center">
              {renderLeaguePreferences()}
            </div>

            <p className="mt-14 mb-14">More sports you can follow.</p>

            <div className="flex flex-wrap gap-2 justify-center">
              {renderSportPreferences()}
            </div>
          </main>

          <Modal
            isOpen={modalTeams}
            onRequestClose={closeModal}
            className="overflow-auto absolute top-0 right-0 left-0 bottom-4 z-40 mr-auto ml-auto w-full bg-black border-none outline-none md:border-yellow-400 md:rounded-md md:border-1 md:w-8/12 lg:w-1/2 md:left-4 md:right-4 md:top-5"
            overlayClassName="fixed z-30 top-0 left-0 right-0 bottom-0 bg-gray-800/95 text-white"
          >
            <main>
              <div className="flex sticky top-0 z-50 p-5 mb-2 w-full h-20 bg-black">
                <div className="justify-center">
                  <div className="w-12">
                    <Icon
                      icon={
                        LEAGUES.find(
                          (league) => league.id === `${currentLeague}`,
                        )?.icon
                      }
                      name={
                        LEAGUES.find(
                          (league) => league.id === `${currentLeague}`,
                        )?.name
                      }
                    />
                  </div>
                </div>
                <div className="flex justify-center items-center px-5 w-full text-xs text-center md:text-sm lg:text-md">
                  Select up to 3 of your favorite teams to follow.
                </div>
                <div className="justify-center">
                  <button
                    type="submit"
                    form="PreferencesForm"
                    className="bg-yellow-300 hover:bg-white btn"
                  >
                    Save
                  </button>
                </div>
              </div>
              {TEAMS[currentLeague + "_teams"] ? (
                <div className="flex flex-wrap gap-4 justify-center items-center mx-2 mt-2">
                  {currentLeague && renderTeamsPreferences(currentLeague)}
                </div>
              ) : (
                <p className="text-center text-white">No Teams</p>
              )}
            </main>
          </Modal>

          <Modal
            isOpen={modalDone}
            onRequestClose={closeModal}
            className="absolute top-1/2 left-1/2 z-50 w-11/12 bg-black rounded-md border border-yellow-400 transform -translate-x-1/2 -translate-y-1/2 outline-none md:w-auto focus:ring-0"
            overlayClassName="fixed z-40 top-0 left-0 right-0 bottom-0 bg-gray-800/95 text-white"
          >
            <main className="relative">
              {isSavingPreferences ? (
                <div className="p-10 text-center">
                  <p> Saving Your Preferences...</p>
                  <div className="flex justify-center items-center">
                    <Loader />
                  </div>
                </div>
              ) : (
                <div className="p-10 text-left">
                  <div className="mb-6">
                    <h2 className="mb-5 text-md md:text-4xl">You're In!</h2>
                    <p className="mb-5 text-lg">
                      All the updates about your favorite teams will be sent
                      directly to your inbox.
                    </p>
                    <p className="text-xs">
                      Come back to this page at any time to update your
                      preferences.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setModalDoneOpen(false);
                      setModalTeamsOpen(false);
                      setModalErrorOpen({
                        open: false,
                        message: "",
                      });
                    }}
                    className="absolute top-5 right-5 btn"
                  >
                    Close
                  </button>
                </div>
              )}
            </main>
          </Modal>

          <Modal
            isOpen={modalError.open}
            onRequestClose={closeModal}
            className="absolute top-1/2 left-1/2 z-50 w-11/12 bg-black rounded-md border border-yellow-400 transform -translate-x-1/2 -translate-y-1/2 outline-none md:w-auto focus:border-current focus:ring-0"
            overlayClassName="fixed z-50 top-0 left-0 right-0 bottom-0 bg-gray-800/95 text-white"
          >
            <main className="relative">
              <div className="p-10 text-center">
                <div className="mt-10 mb-6">{modalError.message}</div>

                <button
                  onClick={() => {
                    setModalDoneOpen(false);
                    setModalErrorOpen({
                      open: false,
                      message: "",
                    });
                  }}
                  className="absolute top-5 right-5 btn"
                >
                  Close
                </button>
              </div>
            </main>
          </Modal>
        </form>
        <Footer />
      </div>
    </FormProvider>
  );
};

export default App;
